/**
 * Words and previews for "Copy a section from another form" — read-only helpers around graftSection.
 *
 * WHY THIS FILE EXISTS
 *   graftSection (libraries/section-copy.js) is shared byte-for-byte with teamio-admin, so it only
 *   returns data. Two screens have to turn that data into something a person can read:
 *     - the builder's SectionCopyModal (views/builder/modal-config-views/SectionCopyModal.vue) previews
 *       every section of the picked form, and the builder handler
 *       (mixins/form-builder/form-builder-event-handler.js) toasts what the copy changed, in words an
 *       office manager understands, so they can fix it before Save;
 *     - Super Admin's "Copy Form Section" panel (pages/Gautam/SuperAdmin.vue) previews the source
 *       section and lists the COPY_FORM_SECTION report (which adds odRemap / branding / autoAssign /
 *       sanitize / backup entries on the server).
 *   One wording for both, so the two screens never describe the same change differently.
 *
 * Import-free with ONE trailing export: cypress/node-tests/forms-section-copy.test.js loads this
 * source, strips the export and runs it.
 */

function isObj(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function sortNum(value) {
    const n = Number(value);
    return isFinite(n) ? n : 0;
}

/** Label/headline HTML -> one line of plain text (labels are rich text in the builder). */
function stripHtml(value) {
    if (value === undefined || value === null) return '';
    return String(value)
        // line-breaking tags become a space, inline ones (<em>, <strong>, <span>) vanish
        .replace(/<(?:br|hr|\/(?:p|div|li|h[1-6]|tr|td|th))\b[^>]*>/gi, ' ')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#0?39;/g, "'")
        .replace(/&amp;/gi, '&')
        .replace(/\s+/g, ' ')
        .trim();
}

/** What a person calls a control: its label as text, else its field name. */
function controlTitle(control) {
    if (!isObj(control)) return '';
    return stripHtml(control.label) || stripHtml(control.name) || '';
}

/**
 * Every section of a parsed ConfigData, in form order, with its fields.
 * @param {Object} config - { sections, rows, controls }
 * @param {Function} [typeName] - control type -> display name (CONTROLS[type].name); defaults to the type
 * @returns {Array<{ id, title, headline, type, isTab, fieldCount,
 *                   groups: Array<{ tabName: (String|null), fields: Array<{ id, label, type, typeName }> }> }>}
 *   `id` is the key in config.sections (what graftSection's sectionId expects). Tab sections get one
 *   group per tab (tabName set); other sections one group with tabName null.
 */
function describeSections(config, typeName) {
    const cfg = isObj(config) ? config : {};
    const sections = isObj(cfg.sections) ? cfg.sections : {};
    const rows = isObj(cfg.rows) ? cfg.rows : {};
    const controls = isObj(cfg.controls) ? cfg.controls : {};
    const nameOfType = typeof typeName === 'function' ? typeName : function (t) { return t; };

    const field = function (id) {
        const control = controls[id];
        if (!isObj(control)) return null;
        return {
            id: id,
            label: controlTitle(control) || '(No label)',
            type: control.type || '',
            typeName: nameOfType(control.type) || control.type || '',
        };
    };
    const fieldsOf = function (ids) {
        return (Array.isArray(ids) ? ids : []).map(field).filter(Boolean);
    };

    return Object.keys(sections)
        .filter(function (key) { return isObj(sections[key]); })
        .map(function (key) { return { key: key, section: sections[key] }; })
        .sort(function (a, b) { return sortNum(a.section.sortOrder) - sortNum(b.section.sortOrder); })
        .map(function (entry) {
            const s = entry.section;
            const isTab = s.type === 'tab';
            const groups = [];
            if (Array.isArray(s.controls) && s.controls.length) {
                groups.push({ tabName: null, fields: fieldsOf(s.controls) });
            }
            (Array.isArray(s.rows) ? s.rows : []).forEach(function (rowId, i) {
                const row = rows[rowId];
                if (!isObj(row)) return;
                const tabName = stripHtml(isObj(row.extendData) ? row.extendData.tabName : '') || ('Tab ' + (i + 1));
                groups.push({ tabName: isTab ? tabName : null, fields: fieldsOf(row.controls) });
            });
            const headline = stripHtml(s.headline);
            return {
                id: entry.key,
                title: headline || '(Untitled section)',
                headline: headline,
                type: s.type || 'normal',
                isTab: isTab,
                fieldCount: groups.reduce(function (n, g) { return n + g.fields.length; }, 0),
                groups: groups,
            };
        });
}

function plural(n, one, many) {
    return n + ' ' + (n === 1 ? one : many);
}

/**
 * A rule/validation names another field by its value key (name, or uniqueId when the name is blank).
 * Show that field's label when the source form has it; a raw "control-<uuid>" never reaches a person.
 */
function referencedFieldTitle(field, sourceConfig) {
    const key = String(field);
    const controls = isObj(sourceConfig) && isObj(sourceConfig.controls) ? sourceConfig.controls : {};
    const ids = Object.keys(controls);
    for (let i = 0; i < ids.length; i++) {
        const c = controls[ids[i]];
        if (!isObj(c)) continue;
        if ((c.name && String(c.name) === key) || ids[i] === key || (c.uniqueId && String(c.uniqueId) === key)) {
            return controlTitle(c) || key;
        }
    }
    return key.indexOf('control-') === 0 ? 'a field' : key;
}

/** One report entry -> one sentence (or null for 'copied', which is the headline, not a change). */
function describeChange(entry, ctx) {
    const context = ctx || {};
    const controls = isObj(context.config) && isObj(context.config.controls) ? context.config.controls : {};
    const fieldTitle = function (id) {
        const title = controlTitle(controls[id]);
        return title ? '"' + title + '"' : 'a copied field';
    };
    const other = function (field) {
        const title = referencedFieldTitle(field, context.sourceConfig);
        return title === 'a field' ? 'a field' : '"' + title + '"';
    };
    if (!isObj(entry)) return null;
    switch (entry.kind) {
        case 'renamed':
            return 'Field "' + entry.from + '" was renamed to "' + entry.to + '" because this form already has a field with that name.';
        case 'odMappingCleared':
            return 'Field ' + fieldTitle(entry.control) + ' no longer saves to the Open Dental field "' + entry.column +
                '" because another field on this form already saves there.';
        case 'ruleDropped':
            if (entry.rule === 'sameAs') {
                return 'Field ' + fieldTitle(entry.on) + ' no longer has to match ' + other(entry.field) +
                    ', because that field is not part of the copied section.';
            }
            return 'The show/hide rule on ' + (entry.onType === 'section' ? 'the copied section' : 'field ' + fieldTitle(entry.on)) +
                ' was removed because it depended on ' + other(entry.field) + ', which is not part of the copied section.';
        case 'ruleLinked': {
            // The rule names a field THIS form already has; it was kept, pointing at that field.
            const title = referencedFieldTitle(entry.field, context.config);
            const target = title === 'a field' ? 'a field on this form' : '"' + title + '" on this form';
            if (entry.rule === 'sameAs') {
                return 'Field ' + fieldTitle(entry.on) + ' must still match ' + target + '. Check that is still right here.';
            }
            return 'The show/hide rule on ' + (entry.onType === 'section' ? 'the copied section' : 'field ' + fieldTitle(entry.on)) +
                ' now follows ' + target + '. Check it shows and hides the way you expect.';
        }
        default:
            return null;
    }
}

/**
 * The builder's toasts for one graft.
 * @param {Object[]} report - graftSection's report
 * @param {{ config?: Object, sourceConfig?: Object }} [ctx] - the grafted config (labels of the copies)
 *   and the source config (labels of fields a dropped rule pointed at)
 * @returns {{ success: String, warnings: String[] }}
 */
function describeCopyReport(report, ctx) {
    const list = Array.isArray(report) ? report : [];
    const copied = list.find(function (e) { return isObj(e) && e.kind === 'copied'; }) || {};
    const headline = stripHtml(copied.sectionHeadline) || 'Untitled section';
    const count = Number(copied.controls) || 0;
    const warnings = list
        .filter(function (e) { return isObj(e) && e.kind !== 'copied'; })
        .map(function (e) { return describeChange(e, ctx); })
        .filter(Boolean);
    return {
        success: 'Section "' + headline + '" copied (' + plural(count, 'field', 'fields') + ')',
        warnings: warnings,
    };
}

/** Short value summary for report fields whose shape the server owns (odRemap, autoAssign...). */
function summarize(value) {
    if (value === undefined || value === null || value === '') return '';
    if (Array.isArray(value)) {
        return value.map(function (v) {
            if (isObj(v)) {
                return Object.keys(v)
                    .map(function (k) { return v[k]; })
                    .filter(function (x) { return x !== null && x !== undefined && typeof x !== 'object' && x !== ''; })
                    .join(' / ');
            }
            return String(v);
        }).filter(Boolean).join('; ');
    }
    if (isObj(value)) {
        return Object.keys(value)
            .map(function (k) { const s = summarize(value[k]); return s === '' ? '' : k + ': ' + s; })
            .filter(Boolean)
            .join(', ');
    }
    return String(value);
}

function countOf(value) {
    if (Array.isArray(value)) return value.length;
    const n = Number(value);
    return isFinite(n) ? n : 0;
}

const REPORT_KIND_LABELS = {
    copied: 'Copied',
    renamed: 'Field renamed',
    odMappingCleared: 'Open Dental mapping cleared',
    ruleDropped: 'Rule removed',
    ruleLinked: 'Rule linked',
    odRemap: 'Open Dental options',
    branding: 'Branding',
    autoAssign: 'Auto-assign',
    sanitize: 'Clean-up',
    backup: 'Backup',
};

/**
 * Super Admin's report table: one { kind, label, text } row per COPY_FORM_SECTION report entry.
 * Entries the server adds (odRemap, branding, autoAssign, sanitize, backup) are described from the
 * fields the plan names, and anything else falls back to a key: value summary — never dropped.
 */
function describeReportEntry(entry, ctx) {
    if (!isObj(entry)) return { kind: 'unknown', label: 'Unknown', text: summarize(entry) };
    const kind = String(entry.kind || 'unknown');
    const label = REPORT_KIND_LABELS[kind] || kind;
    const rest = {};
    Object.keys(entry).forEach(function (k) { if (k !== 'kind') rest[k] = entry[k]; });
    let text = null;
    if (kind === 'copied') {
        text = 'Section "' + (stripHtml(entry.sectionHeadline) || 'Untitled section') + '": ' +
            plural(Number(entry.controls) || 0, 'field', 'fields') +
            (Number(entry.rows) ? ', ' + plural(Number(entry.rows), 'tab', 'tabs') : '') +
            ', position ' + entry.sortOrder + ' on the target form.';
    } else if (kind === 'renamed' || kind === 'odMappingCleared' || kind === 'ruleDropped' || kind === 'ruleLinked') {
        text = describeChange(entry, ctx);
    } else if (kind === 'odRemap') {
        const unmatched = summarize(entry.unmatchedOptions);
        text = plural(countOf(entry.remappedControls), 'field', 'fields') + " re-pointed to the target site's Open Dental lists; " +
            plural(countOf(entry.unmatchedOptions), 'option', 'options') + ' with no match on the target site' +
            (unmatched ? ': ' + unmatched : '') + '.';
    } else if (kind === 'branding') {
        text = 'Practice name / branding replaced ' + plural(countOf(entry.replacementCount), 'time', 'times') + ' inside the copied section.';
    }
    return { kind: kind, label: label, text: text || summarize(rest) || '—' };
}

export { stripHtml, describeSections, describeCopyReport, describeReportEntry, REPORT_KIND_LABELS }
